import {format} from 'date-fns'
import {Trans} from 'react-i18next'
import {useTranslation, TKey} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {FormProvider, useForm} from 'react-hook-form'
import {patientAbsenceFormValuesToPayload} from '../../utils'
import {useStoreEvent} from '../../queries/useStoreEvent'
import {AddPatientAbsenceConfirmationValidationSchema} from '../../formSchemas'
import {DialogStore, useDialogContext} from '@ariakit/react'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'

const getComunicatedOnTimeToLabel = {
    within_48_hours: 'commons:within_48_hours',
    over_48_hours: 'commons:over_48_hours'
} as const satisfies Record<string, TKey>

export const PatientAbsenceConfirmationDialog = ({
    defaultValues,
    dialogStore
}: {
    defaultValues: AddPatientAbsenceConfirmationValidationSchema
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()

    const createDialogStore = useDialogContext()
    if (!createDialogStore) {
        throw new Error('createDialogStore is not found')
    }
    const {mutate: createEvent, isPending} = useStoreEvent({
        onSuccess: () => {
            createDialogStore.setOpen(false)
        }
    })
    const form = useForm<AddPatientAbsenceConfirmationValidationSchema>({
        defaultValues: defaultValues || undefined
    })
    const onSubmit = form.handleSubmit(
        data => {
            createEvent(patientAbsenceFormValuesToPayload(data))
        },
        errors => {
            console.error('Validation errors:', errors)
        }
    )
    const values = form.watch()
    const patient = values.patients?.[0]

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={onSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column" gap={7} fullWidth>
                        <Flexbox direction="column" fullWidth gap={1}>
                            <DialogHeading>{t('agenda:add_patient_absence_confirmation_title')}</DialogHeading>
                            {patient && patient.project ? (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {patient.fullName} -{' '}
                                    <Trans
                                        i18nKey="agenda:pri_n_expiration_date"
                                        values={{
                                            code: patient.project?.code || '-',
                                            date: patient.project?.expirationDate
                                                ? format(new Date(patient.project.expirationDate), 'dd/MM/yyyy')
                                                : '-'
                                        }}
                                    />
                                </Text>
                            ) : (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {patient?.fullName}
                                </Text>
                            )}
                        </Flexbox>
                        <Grid columns={2} columnGap={6} rowGap={2} fullWidth>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:add_absence_start_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {form.watch('startDate') && format(form.watch('startDate'), 'dd/MM/yyyy')}
                            </Text>

                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:add_absence_end_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {form.watch('endDate') && format(form.watch('endDate'), 'dd/MM/yyyy')}
                            </Text>

                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:communication_absence')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {values.communicatedOnTime
                                    ? t(getComunicatedOnTimeToLabel[values.communicatedOnTime])
                                    : '-'}
                            </Text>
                        </Grid>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
